import {
  Alert,
  Autocomplete,
  Button,
  type FilterOptionsState,
  TextField,
  Typography,
  createFilterOptions
} from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import React from 'react';
import { useAutoCreatedRecipesQuery, useUndoAutoRecipesMutation } from 'providers/api';
import { trackEvent } from 'providers/analytics';
import { useScenario } from 'modules/project/providers/scenario';
import { usePage } from 'providers/page';
import { selectableRheology } from 'utils/rheology';
import RecipeForm from '../StopePanelPage/RecipesTab/RecipeForm/RecipeForm';
import { ModelState } from '../hooks/useModelState';
import { PCStope } from '../MineModel/Stopes';
import { generateAllRoutes, routeOptsForProject } from '../MineModel/Route/utils';

type AddAllOption = { addAllStopes: true };
type StopeOption = PCStope | AddAllOption;

const ADD_ALL_SENTINEL: AddAllOption = { addAllStopes: true };
const stopeFilter = createFilterOptions<StopeOption>();

const isAddAllSentinel = (option: StopeOption): option is AddAllOption => (option as AddAllOption).addAllStopes === true;

interface AutoRecipeFormProps {
  modelState: ModelState;
  onComplete: () => void;
}

const AutoRecipeForm = ({ modelState, onComplete }: AutoRecipeFormProps) => {
  const [newRecipeIsOptimised, setNewRecipeIsOptimised] = React.useState(false);
  const stopeData: PCStope[] = React.useMemo(() => {
    const values = Object.values(modelState.model.stopes ?? {});
    const flat = values.flatMap((v: any) => (Array.isArray(v) ? v : [v]));
    return flat.filter((s): s is PCStope => !!s && !!s.data && !!s.data.stopeId);
  }, [modelState.model.stopes]);
  const [stopeId, setStopeId] = React.useState<PCStope[]>([]);
  const [hasStopeError, setStopeError] = React.useState(true);
  const stopeName: string[] = [];
  const isAutoRecipe = true;
  const { activeScenarioId } = useScenario();
  const undoAutoRecipes = useUndoAutoRecipesMutation();
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();
  const projectId = modelState.model.project.entityId;
  const { refetch: refetchAutoCreatedRecipes } = useAutoCreatedRecipesQuery(
    { projectId, scenarioId: activeScenarioId ?? undefined },
    { enabled: false },
  );

  const handleCloseModal = () => onComplete();
  const handleRecipeWarning = () => { };
  const publishUndoForLatestBatch = async () => {
    const { data: autoRecipes } = await refetchAutoCreatedRecipes();
    if (!autoRecipes || autoRecipes.length === 0) {
      return;
    }

    const latestBatchId = autoRecipes.reduce((latest, recipe) => {
      const batchId = recipe.autoRecipeBatchId ?? 0;
      return batchId > latest ? batchId : latest;
    }, 0);

    if (!latestBatchId) {
      return;
    }

    const handleUndo = () => {
      setUndoActionOverride((current) => (current ? {
        ...current,
        enabled: false,
        loading: true,
      } : current));

      undoAutoRecipes.mutate(
        {
          projectId,
          autoRecipeBatchId: latestBatchId,
        },
        {
          onSuccess: () => clearUndoActionOverride(),
          onError: () => {
            setUndoActionOverride({
              enabled: true,
              loading: false,
              onClick: handleUndo,
            });
          },
        },
      );
    };

    setUndoActionOverride({
      enabled: true,
      loading: false,
      onClick: handleUndo,
    });
  };
  const handleSuccess = (): void => {
    trackEvent('recipe_generated', {
      project_id: projectId,
      stope: stopeId.map((s) => s.data.stopeName ?? s.data.stopeId).join(', '),
      stope_count: stopeId.length,
    });
    publishUndoForLatestBatch().catch(() => undefined);
    handleCloseModal();
  };

  const handleChange = (selectedStopes: PCStope[]) => {
    setStopeId(selectedStopes);
    setStopeError(selectedStopes.length === 0);
  };

  const handleNewRecipeIsOptimisedUpdate = (isOptimised: boolean) => {
    setNewRecipeIsOptimised(isOptimised);
  };

  const [inputValue, setInputValue] = React.useState('');
  const filteredStopesRef = React.useRef<PCStope[]>([]);

  const filterOptions = React.useCallback(
    (options: StopeOption[], state: FilterOptionsState<StopeOption>): StopeOption[] => {
      const filtered = stopeFilter(options, state) as PCStope[];
      filteredStopesRef.current = filtered;
      const unselectedCount = filtered.filter(
        (s) => !stopeId.some((sel) => sel.data.stopeId === s.data.stopeId),
      ).length;
      return unselectedCount > 0 ? [ADD_ALL_SENTINEL, ...filtered] : filtered;
    },
    [stopeData, stopeId],
  );

  const pickShortestRouteSilent = (routes: Record<string, any>) => Object.values(routes).reduce((best: any, r: any) => (!best
    || r.routeLength < best.routeLength ? r : best), undefined as any);

  const activeRheology = selectableRheology(modelState.model.rheology);
  const hasRheology = activeRheology.length > 0;

  return (
    <Dialog open={isAutoRecipe} onClose={handleCloseModal}>
      <DialogTitle id="simple-dialog-title">
        <Trans id="Auto Recipe Creation">Auto Recipe Creation</Trans>
      </DialogTitle>

      {!hasRheology ? (
        <Box sx={{ width: 500, m: 2 }}>
          <Alert severity="error">
            <Trans id="No rheology data has been added to this project. Please add rheology data before creating auto recipes.">
              No rheology data has been added to this project. Please add rheology data before creating auto recipes.
            </Trans>
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={handleCloseModal} variant="contained">
              <Trans id="Close">Close</Trans>
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ width: 500, m: 2 }}>
          <Autocomplete<StopeOption, true, false, false>
            multiple
            id="stope-selector"
            options={stopeData}
            value={stopeId}
            disabled={newRecipeIsOptimised}
            inputValue={inputValue}
            onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
            filterOptions={filterOptions}
            getOptionLabel={(option) => {
            if (isAddAllSentinel(option)) return inputValue ? i18n._('Add Matching Stopes') : i18n._('Add All Stopes');
            return option.data?.stopeName ?? option.data?.stopeId ?? '';
          }}
            isOptionEqualToValue={(opt, val) => {
            if (isAddAllSentinel(opt) || isAddAllSentinel(val)) return false;
            return opt.data?.stopeId === val.data?.stopeId;
          }}
            onChange={(_event, newValue) => {
            if (newValue.some(isAddAllSentinel)) {
              const alreadySelectedIds = new Set(stopeId.map((s) => s.data.stopeId));
              const unselected = filteredStopesRef.current.filter((s) => !alreadySelectedIds.has(s.data.stopeId));
              handleChange([...stopeId, ...unselected]);
            } else {
              handleChange(newValue as PCStope[]);
            }
          }}
            disableCloseOnSelect
            renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            if (isAddAllSentinel(option)) {
              return (
                <li key="add-all-stopes" {...optionProps}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                    {inputValue ? i18n._('Add Matching Stopes') : i18n._('Add All Stopes')}
                  </Typography>
                </li>
              );
            }
            return (
              <li key={key} {...optionProps}>
                {option.data?.stopeName ?? option.data?.stopeId ?? ''}
              </li>
            );
          }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n._('Select Stopes')}
                placeholder={i18n._('Select Stopes')}
                error={hasStopeError}
              />
          )}
          />

          <Box sx={{ mt: 2 }}>
            <RecipeForm
              model={modelState.model}
              selectedDataset={activeRheology[0]}
              stopeId={stopeId}
              stopeName={stopeName}
              isAutoRecipe={isAutoRecipe}
              isOptimised={newRecipeIsOptimised}
              checkForWarnings={() => {
              if (!stopeId[0] || !modelState.model.activeBackfillPlant || !modelState.model.spools || !modelState.model.pipes || !modelState.model.nodes) return [];

              try {
                const { routeObjects: localRoutes } = generateAllRoutes(
                  modelState.model.activeBackfillPlant,
                  modelState.model.spools,
                  stopeId[0],
                  Object.values(modelState.model.pipes),
                  routeOptsForProject(modelState.model.project),
                  modelState.model.nodes,
                  modelState.model.pipes,
                );
                const shortest = pickShortestRouteSilent(localRoutes);
                return shortest?.findWarnings?.() ?? [];
              } catch {
                return [];
              }
            }}
              onWarning={handleRecipeWarning}
              onSuccess={handleSuccess}
              onCancel={handleCloseModal}
              updateIsOptimised={handleNewRecipeIsOptimisedUpdate}
            />
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default AutoRecipeForm;
